import { useEffect, useRef } from 'react';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { LICENSE_PERIOD_OPTIONS } from '../constants';

interface Props {
  value: number;
  onChange: (months: number) => void;
  /** Product-specific available periods. Falls back to global LICENSE_PERIOD_OPTIONS. */
  availablePeriods?: number[];
  /** If true, the select dropdown opens automatically on mount. */
  autoOpen?: boolean;
  /** Called when the select loses focus without a selection. */
  onBlur?: () => void;
}

export default function LicensePeriodEditor({
  value,
  onChange,
  availablePeriods,
  autoOpen,
  onBlur,
}: Props) {
  const periods =
    availablePeriods && availablePeriods.length > 0
      ? [...availablePeriods].sort((a, b) => a - b)
      : [...LICENSE_PERIOD_OPTIONS];

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoOpen && ref.current) {
      // Trigger a click on the select to open the dropdown
      const selectEl = ref.current.querySelector('[role="combobox"]') as HTMLElement | null;
      if (selectEl) {
        // Small delay so MUI can finish mounting
        requestAnimationFrame(() => selectEl.click());
      }
    }
  }, [autoOpen]);

  return (
    <TextField
      ref={ref}
      select
      size="small"
      label="Period"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      onBlur={onBlur}
      sx={{ minWidth: 100, width: 'fit-content' }}
      data-testid="license-period-editor"
      SelectProps={{
        onClose: () => {
          // After dropdown closes, fire onBlur so parent can exit edit mode
          // Use a small delay so onChange fires first if user selected a value
          if (onBlur) setTimeout(onBlur, 0);
        },
      }}
    >
      {periods.map((months) => (
        <MenuItem key={months} value={months}>
          {months} months
        </MenuItem>
      ))}
    </TextField>
  );
}
